import { useEffect, useRef } from "react";

interface WinCard {
  num: string;
  accentColor: string;
  metric: string;
  metricSub?: string;
  title: string;
  desc: string;
  iconClass: string;
}

const wins: WinCard[] = [
  {
    num: "01",
    accentColor: "#C17D3C",
    iconClass: "ri-money-dollar-circle-line",
    metric: "$100K+",
    metricSub: "MRR recovered",
    title: "Turned churn risk into retained revenue",
    desc: "Led customer discovery at Intuit / TSheets to redesign a broken billing migration experience — conducting in-depth interviews that reshaped the entire flow.",
  },
  {
    num: "02",
    accentColor: "#C17D3C",
    iconClass: "ri-arrow-up-line",
    metric: "90%",
    metricSub: "Retention rate",
    title: "Built the feature that made customers stay",
    desc: "Expanded TSheets from a single SKU to two tiers. Features drove 90% retention when adopted — partnered with onboarding to close the activation gap.",
  },
  {
    num: "03",
    accentColor: "#8B7355",
    iconClass: "ri-map-pin-line",
    metric: "Zero",
    metricSub: "Worker dissatisfaction increase",
    title: "GPS enforcement without the backlash",
    desc: "Used IDEO Design Thinking to satisfy both business owners and employees. Double-digit reduction in time theft — zero increase in worker dissatisfaction.",
  },
  {
    num: "04",
    accentColor: "#4A6741",
    iconClass: "ri-computer-line",
    metric: "1",
    metricSub: "Unified platform",
    title: "HP LaserJet platform + 3rd-party ecosystem",
    desc: "Led a large cross-functional team to ship a unified login platform for all HP LaserJet devices — launching Private Print and Scan to Cloud as anchor apps.",
  },
  {
    num: "05",
    accentColor: "#4A6741",
    iconClass: "ri-bar-chart-2-line",
    metric: "Data",
    metricSub: "First culture",
    title: "Pioneered analytics-driven prioritization",
    desc: "Introduced real customer usage metrics as a new organizational muscle — shifting roadmap decisions from intuition to evidence at HP Inc.",
  },
];

function WinCard({ card, index }: { card: WinCard; index: number }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(32px)";
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition = `opacity 0.7s ease-out ${index * 0.1}s, transform 0.7s ease-out ${index * 0.1}s`;
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={ref}
      className="flex flex-col cursor-default transition-transform duration-300"
      style={{
        minHeight: "380px",
        backgroundColor: "#1C1510",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: "16px",
        padding: "2rem",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = "translateY(-4px)";
        (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(193,125,60,0.25)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
        (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(255,255,255,0.06)";
      }}
    >
      {/* Top row: number + icon */}
      <div className="flex items-center justify-between mb-8">
        <span
          className="font-dm text-[11px] uppercase tracking-[0.14em]"
          style={{ color: "rgba(250,248,244,0.2)" }}
        >
          {card.num}
        </span>
        <div
          className="flex items-center justify-center"
          style={{
            width: "38px",
            height: "38px",
            borderRadius: "10px",
            backgroundColor: `${card.accentColor}18`,
            border: `1px solid ${card.accentColor}30`,
          }}
        >
          <i className={`${card.iconClass} text-base`} style={{ color: card.accentColor }} />
        </div>
      </div>

      {/* Metric */}
      <div className="mb-5">
        <p
          className="font-lora font-semibold leading-none mb-1"
          style={{ fontSize: "clamp(2.2rem, 4vw, 2.8rem)", color: "#FAF8F4" }}
        >
          {card.metric}
        </p>
        {card.metricSub && (
          <p className="font-dm text-[12px] uppercase tracking-[0.08em]" style={{ color: card.accentColor }}>
            {card.metricSub}
          </p>
        )}
      </div>

      {/* Divider */}
      <div className="mb-5" style={{ height: "1px", backgroundColor: "rgba(255,255,255,0.06)" }} />

      {/* Title + desc */}
      <p className="font-dm font-medium text-[14px] leading-snug mb-3" style={{ color: "#FAF8F4" }}>
        {card.title}
      </p>
      <p className="font-dm text-[13px] leading-[1.65]" style={{ color: "rgba(250,248,244,0.4)" }}>
        {card.desc}
      </p>
    </div>
  );
}

export default function FeaturedWins() {
  const headerRef = useRef<HTMLDivElement>(null);
  const patentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    [headerRef.current, patentRef.current].forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(24px)";
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.style.transition = `opacity 0.8s ease-out ${i * 0.15}s, transform 0.8s ease-out ${i * 0.15}s`;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(el);
    });
  }, []);

  return (
    <section id="wins" className="py-28 lg:py-36 overflow-hidden" style={{ backgroundColor: "#0E0B07" }}>
      <div className="mx-auto px-8 lg:px-16 mb-12" style={{ maxWidth: "1200px" }}>
        <div ref={headerRef}>
          {/* Numbered eyebrow */}
          <div className="flex items-center gap-4 mb-6">
            <span className="font-dm text-[11px] uppercase tracking-[0.16em]" style={{ color: "#C17D3C" }}>01</span>
            <div style={{ width: "40px", height: "1px", backgroundColor: "#C17D3C", opacity: 0.5 }} />
            <span className="font-dm text-[11px] uppercase tracking-[0.16em]" style={{ color: "rgba(250,248,244,0.35)" }}>Featured wins</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="font-lora font-semibold leading-[1.15] tracking-tight"
              style={{ fontSize: "clamp(2rem, 3.5vw, 2.8rem)", color: "#FAF8F4" }}
            >
              Results that speak<br />for themselves.
            </h2>
            <p
              className="font-dm text-[14px] leading-[1.7] lg:text-right"
              style={{ color: "rgba(250,248,244,0.4)", maxWidth: "340px" }}
            >
              A career built on measurable outcomes — from revenue recovery to product innovation to team transformation.
            </p>
          </div>
        </div>
      </div>

      {/* Responsive grid cards */}
      <div className="mx-auto px-8 lg:px-16" style={{ maxWidth: "1200px" }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {wins.map((card, i) => (
            <WinCard key={card.num} card={card} index={i} />
          ))}

          {/* Patent card — last in grid */}
          <div
            ref={patentRef}
            className="flex flex-col justify-between cursor-default"
            style={{
              minHeight: "380px",
              background: "linear-gradient(145deg, #2C2418 0%, #1C1510 100%)",
              border: "1px solid rgba(193,125,60,0.2)",
              borderRadius: "16px",
              padding: "2rem",
            }}
          >
            <div>
              <div className="flex items-center justify-between mb-8">
                <span className="font-dm text-[11px] uppercase tracking-[0.14em]" style={{ color: "rgba(250,248,244,0.2)" }}>06</span>
                <div
                  className="flex items-center justify-center"
                  style={{ width: "38px", height: "38px", borderRadius: "10px", backgroundColor: "rgba(193,125,60,0.12)", border: "1px solid rgba(193,125,60,0.25)" }}
                >
                  <i className="ri-award-line text-base" style={{ color: "#C17D3C" }} />
                </div>
              </div>
              <p className="font-dm text-[11px] uppercase tracking-[0.1em] mb-2" style={{ color: "#C17D3C" }}>US Patent</p>
              <p className="font-lora font-semibold text-[2rem] leading-none mb-1" style={{ color: "#FAF8F4" }}>#11438733</p>
            </div>
            <div>
              <div className="mb-4" style={{ height: "1px", backgroundColor: "rgba(255,255,255,0.06)" }} />
              <p className="font-dm font-medium text-[14px] leading-snug mb-3" style={{ color: "#FAF8F4" }}>
                Determining location based on nearby devices
              </p>
              <p className="font-dm text-[13px] leading-[1.65]" style={{ color: "rgba(250,248,244,0.4)" }}>
                A novel approach to proximity-based location determination — contributing to the GPS product line at Intuit / TSheets.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
